import React from 'react';

/**
 * Props interface for the RequestTypeSelector component
 */
interface RequestTypeSelectorProps {
  // Current selection state
  selectedRequestType: 'op' | 'urgent' | null;
  
  // Callback for when user selects a request type
  onRequestTypeSelection: (requestType: 'op' | 'urgent') => void;
  
  // Loading state to disable interaction
  loading?: boolean;
  isLoadingState?: boolean;
}

/**
 * Individual Request Type Option Component
 * Renders a single selectable option with radio button and details
 */
interface RequestTypeOptionProps {
  type: 'op' | 'urgent';
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}

const RequestTypeOption: React.FC<RequestTypeOptionProps> = ({
  type,
  title,
  description,
  selected,
  onClick,
  disabled = false
}) => (
  <div 
    className={`chatbot-request-type-option ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
    onClick={disabled ? undefined : onClick}
    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
  >
    {/* Radio Button */}
    <div className="chatbot-request-type-radio">
      <input 
        type="radio" 
        name="requestType" 
        value={type} 
        checked={selected}
        onChange={disabled ? undefined : onClick}
        disabled={disabled}
      />
    </div>
    
    {/* Option Details */}
    <div className="chatbot-request-type-details">
      <div className="chatbot-request-type-title">
        {title}
      </div>
      <div className="chatbot-request-type-description">
        {description}
      </div>
    </div>
  </div>
);

/**
 * RequestTypeSelector Component
 * 
 * Displays the request type selection interface allowing users to choose between:
 * - OP Request for Next Year (strategic planning)
 * - Urgent In-Year Request (immediate needs)
 * 
 * Features:
 * - Radio button selection interface
 * - Clear descriptions for each option
 * - Visual feedback for selected state
 * - Disabled state during loading
 * - Dynamic year display based on current date
 * 
 * This component extracts the request type selection logic from the main FillyOnIris component
 * to improve maintainability and reusability.
 */
export const RequestTypeSelector: React.FC<RequestTypeSelectorProps> = ({
  selectedRequestType,
  onRequestTypeSelection,
  loading = false,
  isLoadingState = false
}) => {
  // Calculate years for display
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;
  
  // Determine if component should be disabled
  const isDisabled = loading || isLoadingState;

  return (
    <div className="chatbot-request-type-selection">
      <div className="chatbot-request-type-content">
        <div className="chatbot-request-type-options">
          {/* OP Request Option */}
          <RequestTypeOption
            type="op"
            title={`📋 OP Request for Next Year (${nextYear})`}
            description="Strategic planning request for next year's operating plan. Will be prioritized and planned for future implementation."
            selected={selectedRequestType === 'op'}
            onClick={() => onRequestTypeSelection('op')}
            disabled={isDisabled}
          />
          
          {/* Urgent Request Option */}
          <RequestTypeOption
            type="urgent"
            title={`🚨 Urgent In-Year Request (${currentYear})`}
            description="Critical request that needs immediate attention and implementation within the current year."
            selected={selectedRequestType === 'urgent'}
            onClick={() => onRequestTypeSelection('urgent')}
            disabled={isDisabled}
          />
        </div>
        
        {/* Loading indicator or helper text */}
        {isDisabled && (
          <div className="chatbot-request-type-loading">
            <p>Please wait while processing...</p>
          </div>
        )}
        
        {!isDisabled && !selectedRequestType && (
          <div className="chatbot-request-type-helper">
            <p>Please select the type of request you'd like to submit to continue.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestTypeSelector;
